import {
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  Dropdown,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/joy';
import {
  ArrowCounterClockwise,
  ChatCircle,
  ChatsCircle,
  CheckCircle,
  DotsThree,
  MagnifyingGlass,
  Plus,
  X,
} from '@phosphor-icons/react';
import * as DialogPrimitives from '@radix-ui/react-dialog';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Mention, MentionsInput } from 'react-mentions';
import { useParams } from 'react-router-dom';
import useSWRInfinite from 'swr/infinite';
import { useDebounce } from 'use-debounce';
import { z } from 'zod';

import { useAppSelector } from '../../../../../hooks/global/typedReduxHooks';

import { mentionListStyle, mentionStyle } from '~/assets/styles/comments';
import trimText from '~/assets/utils/trimText';
import { Button } from '~/components/ui/button';
import { Card, CardOverflow } from '~/components/ui/card';
import { Dialog, DialogClose, DialogTrigger } from '~/components/ui/dialog';
import { InputField } from '~/components/ui/input';
import { Option, SelectField } from '~/components/ui/select';
import { CLOSED_REPORTS_STATUS_BOUNDARY } from '~/constants/reportConstants';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { CreateCommentForm } from '~/pages/customer/reports/report-details/report-comments/CommentForm';
import { ReportService } from '~/services/api/reports/endpoints';
import { ReportComment } from '~/services/api/reports/types';
import { useWrappedMutation } from '~/services/api/rest-api';
import {
  isFetchBaseQueryError,
  StatusCodeErrorBanner,
} from '~/services/api/rest-api-error';
import { useCurrentUser } from '~/services/api/users/endpoints';
import { stringToColor } from '~/services/commentsService';
import { useForm } from '~/utils/forms';
import { store } from '~/utils/store';

export const useInfiniteReportComments = ({
  search,
  mentionsOnly,
  isEnabled = true,
}: {
  search: string;
  mentionsOnly?: boolean;
  isEnabled?: boolean;
}) => {
  const referenceId = useParams().reportId ?? '';
  const infiniteQuery = useSWRInfinite(
    (pageIndex, previousPageData) => {
      // Do not fetch when the hook is not enabled
      if (!referenceId || !isEnabled) return null;

      if (previousPageData && !previousPageData.length) return null; // reached the end
      return {
        key: 'report-comments',
        commentPage: pageIndex,
        search,
        mentionsOnly,
      }; // SWR key
    },
    async (args) => {
      const query = store.dispatch(
        ReportService.endpoints.reportComments.initiate({
          referenceId: referenceId,
          search: args.search,
          mentionsOnly: JSON.stringify(args.mentionsOnly),
          params: { page: args.commentPage + 1 },
        }),
      );
      const reportComments = await query;
      query.unsubscribe();
      const is404 =
        isFetchBaseQueryError(reportComments.error) &&
        reportComments.error.status === 404;
      if (is404) {
        return [];
      }

      if (reportComments.isError || !reportComments.data) {
        throw new Error('Failed to fetch report comments');
      }

      return reportComments.data.results;
    },
    {
      revalidateOnFocus: false,
      revalidateOnMount: true,
    },
  );

  const { data, size, isValidating, isLoading } = infiniteQuery;
  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isEmpty = data?.[0]?.length === 0;
  const latestPage =
    data && Array.isArray(data[data.length - 1]) ? data[data.length - 1] : [];
  const isEndOfList = isEmpty || (!!data && latestPage.length === 0);
  const isRefreshing = isValidating && !!data && data.length === size;

  return {
    ...infiniteQuery,
    isEndOfList,
    isEmpty,
    isLoadingMore,
    isRefreshing,
  };
};

const commentParamsSchema = z.object({
  search: z.string(),
  filterBy: z.nativeEnum({
    'all-comments': 'all-comments',
    mentions: 'mentions',
  } as const),
  isCreatingComment: z.boolean(),
});

export function ReportComments() {
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  const { is_read_only_report: isReadOnlyReport } = useAppSelector(
    (state) => state.reportData.userAccessRoles,
  );
  const reportData = useAppSelector((state) => state.reportData.report);
  const isDisabled =
    isReadOnlyReport ||
    (reportData.status <= CLOSED_REPORTS_STATUS_BOUNDARY ? false : true);
  const { referenceId, isDescriptionEditable } = useAppSelector((state) => state.reportData.report);

  const form = useForm({
    schema: commentParamsSchema,
    defaultValues: {
      search: '',
      filterBy: 'all-comments',
      isCreatingComment: false,
    },
  });
  const [debouncedSearch] = useDebounce(form.watch('search'), 500);
  const filterBy = form.watch('filterBy');

  const {
    data,
    isEmpty,
    isEndOfList,
    isRefreshing,
    isLoading,
    isLoadingMore,
    error,
    mutate,
    setSize,
  } = useInfiniteReportComments({
    search: debouncedSearch,
    mentionsOnly: filterBy === 'mentions',
    isEnabled: open,
  });

  const reportComments =
    data?.reduce(
      (all, current) => [...all, ...current],
      [] as ReportComment[],
    ) ?? [];

  const handleRefreshClicked = () => {
    mutate(undefined, { revalidate: true });
  };

  const isCreatingComment = form.watch('isCreatingComment');

  return (
    <Dialog
      modal={false}
      defaultOpen={open}
      onOpenChange={setOpen}
      isDismissable={true}
    >
      <DialogTrigger asChild>
        <Button
          color='neutral'
          variant='soft'
          startDecorator={<ChatCircle />}
          fullWidth
          disabled={isDisabled || isDescriptionEditable}
        >
          {intl.formatMessage({ defaultMessage: 'Add Comment' })}
        </Button>
      </DialogTrigger>
      <DialogPrimitives.DialogPortal>
        <div className='fixed inset-0 overflow-hidden isolate z-50'>
          <div className='pointer-events-none fixed inset-y-0 right-0 flex max-w-full'>
            <div className='pointer-events-auto relative w-screen max-w-sm'>
              <div className='flex h-full flex-col p-2'>
                <DialogPrimitives.Content
                  className={clsx([
                    'data-[state=open]:animate-slideLeftAndFade ',
                  ])}
                  asChild
                >
                  <Card size='md' sx={{ height: '100%' }} className='shadow-xl'>
                    <div className='flex flex-row justify-end gap-2'>
                      <Button
                        variant='plain'
                        color='neutral'
                        startDecorator={<ArrowCounterClockwise />}
                        onClick={handleRefreshClicked}
                      >
                        <FormattedMessage defaultMessage='Refresh page' />
                      </Button>
                      <DialogClose asChild>
                        <Button
                          variant='plain'
                          color='neutral'
                          startDecorator={<X />}
                        >
                          <FormattedMessage defaultMessage='Close' />
                        </Button>
                      </DialogClose>
                    </div>
                    {error && (
                      <div className='flex items-center justify-center px-4 py-16'>
                        <div>
                          <div>
                            <Typography level='title-lg'>
                              <FormattedMessage defaultMessage='Error' />
                            </Typography>
                            <Typography>
                              <FormattedMessage defaultMessage='Failed to load comments. Try again.' />
                            </Typography>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className='grid gap-2'>
                      <div className='flex justify-between items-center gap-2'>
                        <Typography level='title-md'>
                          <FormattedMessage defaultMessage='Comments' />
                        </Typography>
                        <SelectField
                          control={form.control}
                          field='filterBy'
                          variant='plain'
                        >
                          <Option value='all-comments'>
                            <FormattedMessage defaultMessage='All comments' />
                          </Option>
                          <Option value='mentions'>
                            <FormattedMessage defaultMessage='Mentions' />
                          </Option>
                        </SelectField>
                      </div>
                      <InputField
                        control={form.control}
                        field='search'
                        startDecorator={<MagnifyingGlass />}
                        placeholder='Search comments'
                      />
                    </div>
                    <Divider />
                    {isCreatingComment ? (
                      <CreateCommentForm
                        defaultValues={{ referenceId }}
                        onCommentCreated={() => mutate()}
                        onCommentDiscarded={() =>
                          form.setValue('isCreatingComment', false)
                        }
                      />
                    ) : (
                      <Button
                        color='neutral'
                        variant='outlined'
                        onClick={() => form.setValue('isCreatingComment', true)}
                        startDecorator={<Plus />}
                      >
                        <FormattedMessage defaultMessage='Add Comment' />
                      </Button>
                    )}
                    <div className='flex flex-col flex-1 gap-y-3 overflow-y-auto pb-20'>
                      {isRefreshing && (
                        <div className='sticky top-0 bottom-0 z-10 p-2 inset-x-0 flex justify-center'>
                          <Button size='sm' loading>
                            {intl.formatMessage({
                              defaultMessage: 'Refreshing Comments',
                            })}
                          </Button>
                        </div>
                      )}
                      <ul className='flex flex-col gap-2'>
                        {reportComments.map((comment, index) => (
                          <li key={index}>
                            <Comment comment={comment} />
                          </li>
                        ))}
                      </ul>
                      {isLoading && (
                        <Divider>
                          <CircularProgress size='sm' />
                        </Divider>
                      )}
                      {!isLoading && !isEndOfList && (
                        <Divider>
                          <Button
                            variant='plain'
                            loading={isLoadingMore}
                            onClick={() => setSize((prev) => prev + 1)}
                          >
                            {isLoadingMore
                              ? intl.formatMessage({
                                  defaultMessage: 'Loading More',
                                })
                              : intl.formatMessage({
                                  defaultMessage: 'Load More',
                                })}
                          </Button>
                        </Divider>
                      )}

                      {isEmpty && (
                        <div className='flex-1 flex items-center justify-center'>
                          <div className='space-y-2 flex flex-col justify-center items-center'>
                            <ChatsCircle size={40} />
                            <Typography level='body-sm' className='text-center'>
                              {intl.formatMessage({
                                defaultMessage: 'No comments yet, add now.',
                              })}
                            </Typography>
                          </div>
                        </div>
                      )}
                      {!isEmpty && isEndOfList && (
                        <Divider>
                          <Chip variant='soft' color='neutral' size='sm'>
                            {intl.formatMessage({
                              defaultMessage: 'Reached end of list',
                            })}
                          </Chip>
                        </Divider>
                      )}
                    </div>
                  </Card>
                </DialogPrimitives.Content>
              </div>
            </div>
          </div>
        </div>
      </DialogPrimitives.DialogPortal>
    </Dialog>
  );
}

const mentionStyles = {
  read: {
    list: mentionListStyle,
    mention: mentionStyle,
  },
  unread: {
    list: {
      ...mentionListStyle,
      control: { ...mentionListStyle.control, fontWeight: 600 },
    },
    mention: { ...mentionStyle, fontWeight: 600 },
  },
};
export function Comment({
  comment,
  isReply = false,
}: {
  comment: ReportComment;
  isReply?: boolean;
}) {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const { mutate } = useInfiniteReportComments({
    search: '',
    mentionsOnly: false,
    isEnabled: true,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFullText, setShow] = useState(false);
  const [isReplying, setIsReplying] = useState(false);

  const id = useCurrentUser().data?.id ?? '';
  const referenceId = useAppSelector(
    (state) => state.reportData.report.referenceId,
  );

  const markAsRead = useWrappedMutation(
    ReportService.endpoints.markCommentRead.useMutation(),
  );
  const resolveComment = useWrappedMutation(
    ReportService.endpoints.markCommentResolve.useMutation(),
  );
  const deleteComment = useWrappedMutation(
    ReportService.endpoints.deleteComment.useMutation(),
  );

  const currentUnread = !comment.has_seen_users.some((userID) => userID === id);
  const isUnread = markAsRead.state.isLoading ? !currentUnread : currentUnread;

  const currentResolved =
    'is_resolved' in comment ? comment.is_resolved : false;
  const isResolved = resolveComment.state.isLoading
    ? !currentResolved
    : currentResolved;

  const [displayText] = trimText(comment.content);

  async function onMarkAsReadHandler() {
    await markAsRead.mutateAsync(
      {
        commentId: comment.id,
        referenceId: referenceId,
      },
      {
        onSuccess: () => mutate(),
        onError: () => {
          createToastNotification('Failed to mark comment as read', 'error');
        },
      },
    );
  }
  async function onDeleteHandler() {
    await deleteComment.mutateAsync(
      {
        commentId: comment.id,
        referenceId,
      },
      {
        onSuccess: () => mutate(),
        onError: () => {
          createToastNotification('Failed to delete comment', 'error');
        },
      },
    );
  }
  async function onMarkAsResolvedHandler() {
    await resolveComment.mutateAsync(
      {
        commentId: comment.id,
        referenceId,
        resolved: true,
      },
      {
        onSuccess: () => mutate(),
        onError: () => {
          createToastNotification('Failed to resolve comment', 'error');
        },
      },
    );
  }

  const mutationError = [resolveComment, markAsRead, deleteComment].find(
    (mutation) => mutation.state.error,
  )?.state.error;

  return (
    <Card
      size='sm'
      variant={isReply ? 'plain' : 'outlined'}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: isReply ? 0 : undefined,
        backgroundColor: (theme) =>
          isUnread ? theme.palette.secondary[50] : undefined,
      }}
    >
      <div>
        <StatusCodeErrorBanner size='sm' error={mutationError} />
        <div className='flex min-w-0 items-center gap-3 justify-between'>
          <div className='w-full flex min-w-0 items-center gap-2'>
            <Avatar
              alt={comment.author_details.full_name ?? 'author_details'}
              sx={{
                '--Avatar-size': (theme) => theme.spacing(3),
                backgroundColor: stringToColor(
                  comment.author_details.full_name ?? 'Admin',
                ),
                flexShrink: 0,
              }}
              size='sm'
              variant='solid'
            >
              {comment.author_details.full_name
                ? comment.author_details.full_name[0]
                : 'U'}
            </Avatar>
            <div className='w-full min-w-0 flex items-center gap-1'>
              <Tooltip title={comment.author_details.full_name ?? 'User'}>
                <Typography level='title-sm' className='truncate min-w-0'>
                  {comment.author_details.full_name ?? 'User'}
                </Typography>
              </Tooltip>
              <span className='text-3xl' style={{ lineHeight: 0 }}>
                &middot;
              </span>
              <Typography level='body-xs' className='shrink-0'>
                {format(new Date(comment.updated_at), 'MMM dd, hh:mm aa')}
              </Typography>
            </div>
          </div>
          <div className='flex shrink-0'>
            <Dropdown>
              <MenuButton
                slots={{ root: IconButton }}
                slotProps={{
                  root: { size: 'sm' },
                }}
              >
                <DotsThree />
              </MenuButton>

              <Menu size='sm' placement='bottom-end'>
                <MenuItem
                  onClick={() => onMarkAsReadHandler()}
                  disabled={!isUnread}
                >
                  <FormattedMessage defaultMessage='Mark as read' />
                </MenuItem>
                {id === comment.author_details.id && (
                  <MenuItem onClick={() => onDeleteHandler()} color='danger'>
                    <FormattedMessage defaultMessage='Delete' />
                  </MenuItem>
                )}
              </Menu>
            </Dropdown>
            <Tooltip
              title={
                isResolved
                  ? intl.formatMessage({
                      defaultMessage: 'Resolved',
                    })
                  : intl.formatMessage({
                      defaultMessage: 'Mark comment as resolved',
                    })
              }
            >
              <span className={clsx(comment.parent ? 'hidden' : '')}>
                <IconButton
                  size='sm'
                  variant='soft'
                  onClick={() => onMarkAsResolvedHandler()}
                  disabled={resolveComment.state.isLoading || isResolved}
                  color={isResolved ? 'success' : 'neutral'}
                  sx={{
                    backgroundColor: 'transparent',
                    '&:disabled': {
                      backgroundColor: isResolved ? 'transparent' : undefined,
                      color: (theme) =>
                        isResolved
                          ? `${theme.palette.success[700]} !important`
                          : undefined,
                    },
                  }}
                >
                  <CheckCircle weight={isResolved ? 'fill' : undefined} />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
        <div className='pl-7'>
          <MentionsInput
            value={showFullText ? comment.content : displayText}
            className='control'
            disabled
            style={
              isUnread ? mentionStyles.unread.list : mentionStyles.read.list
            }
          >
            <Mention
              trigger='@'
              data={[]}
              style={
                isUnread
                  ? mentionStyles.unread.mention
                  : mentionStyles.read.mention
              }
            />
          </MentionsInput>
          {displayText !== comment.content && (
            <div className='py-2'>
              <Button
                size='sm'
                color='primary'
                variant='plain'
                onClick={() => setShow((value) => !value)}
              >
                {showFullText
                  ? intl.formatMessage({ defaultMessage: 'See less' })
                  : intl.formatMessage({ defaultMessage: 'See more' })}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div>
        {isExpanded && comment.replies.length > 0 && (
          <ul className='pl-5 border-t border-zinc-100 divide-y divide-zinc-200 max-h-96 overflow-y-auto'>
            {comment.replies.map((reply) => (
              <li key={reply.id}>
                <Comment comment={reply} isReply />
              </li>
            ))}
          </ul>
        )}
        {comment.replies.length !== 0 && (
          <Divider>
            <Button
              variant='plain'
              size='sm'
              color='neutral'
              onClick={() => {
                setIsExpanded((prev) => !prev);
                setIsReplying(true);
              }}
              sx={{
                p: 0,
                backgroundColor: 'transparent !important',
                '&:hover': { color: (theme) => theme.palette.neutral[500] },
              }}
            >
              {isExpanded ? (
                <FormattedMessage defaultMessage='Hide Replies' />
              ) : (
                <FormattedMessage
                  defaultMessage='Show Replies ({replyCount, plural, =0 {0 replies} one {# reply} other {# replies}})'
                  values={{
                    replyCount: comment.replies.length,
                  }}
                />
              )}
            </Button>
          </Divider>
        )}
      </div>
      {!isReply && (
        <nav className='flex justify-end'>
          {!isReplying && (
            <Button
              size='sm'
              color='neutral'
              variant='plain'
              onClick={() => setIsReplying(true)}
            >
              {intl.formatMessage({ defaultMessage: 'Reply' })}
            </Button>
          )}
        </nav>
      )}

      {isReplying && (
        <CardOverflow bleed>
          <div className='px-4 pb-4 pt-2 bg-white'>
            <CreateCommentForm
              defaultValues={{
                parentId: comment.id,
                referenceId,
              }}
              onCommentCreated={() => {
                mutate();
                setIsReplying(false);
              }}
              onCommentDiscarded={() => {
                setIsReplying(false);
              }}
            />
          </div>
        </CardOverflow>
      )}
    </Card>
  );
}
